import type { ComposerTranslation } from 'vue-i18n';
import { FieldValueSource } from '../types/workflows';
import { type M3Field } from '../types/m3';

export const GENERAL_TRANSACTIONS_PROGRAM_NAME = 'GENERAL';
export const M3_SYSTEM = 'M3';
export const DEFAULT_ROWS_TO_PROCESS = 100;
export const RELATED_ITEMS_HIGHLIGHT_DURATION = 500;

export const OUTPUT_PARAMETER_VALUE_SEPARATOR = '.output.';
export const INPUT_PARAMETER_VALUE_SEPARATOR = '.input.';
export const RESULT_MESSAGE_FIELD = 'MSG_COL';


export const M3_COMPANY_DEFAULT_FIELD = 'M3Company';
export const M3_DIVISION_DEFAULT_FIELD = 'M3Division';
export const DEFAULT_INPUT_FIELD_ID_PREFIX = 'defaultInput_';

export const DEFAULT_INPUTS_DATA_CONVERTERS = {
    [M3_COMPANY_DEFAULT_FIELD]: (value: string | number): string | number => {
        return parseInt(value + '');
    }
} as Record<string, (value: string | number) => string | number>;

export const SOURCES_ICONS = {
    [FieldValueSource.CLIENT]: 'pi pi-user',
    [FieldValueSource.API]: 'pi pi-link',
    [FieldValueSource.EXCEL]: 'pi pi-file-excel',
    [FieldValueSource.CONSTANT]: 'pi pi-box',
    [FieldValueSource.EVENT]: 'pi pi-link',
    [FieldValueSource.TAG]: 'pi pi-tag',
    [FieldValueSource.CONTEXT]: 'pi pi-calendar'
} as Record<FieldValueSource, string>;

export const SOURCES_TOOLTIPS = {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [FieldValueSource.CLIENT]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return undefined;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [FieldValueSource.API]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return undefined;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [FieldValueSource.EXCEL]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return undefined;
    },
    [FieldValueSource.CONSTANT]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return `${t('workflows.m3Api.value')}: ${fieldConfig!.value}`;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [FieldValueSource.EVENT]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return undefined;
    },
    [FieldValueSource.TAG]: (fieldConfig: M3Field, t) => {
        return `${t('workflows.m3Api.value')}: ${fieldConfig!.value}`;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [FieldValueSource.CONTEXT]: (fieldConfig: M3Field, t: ComposerTranslation) => {
        return undefined;
    }
} as Record<FieldValueSource, (fieldConfig: M3Field, t: ComposerTranslation) => string | undefined>;

export const DEFAULT_INPUTS_ALLOWED_SOURCES = [
    FieldValueSource.CLIENT, FieldValueSource.CONSTANT, FieldValueSource.EMPTY
];

const STANDARD_SOURCES_LOCALIZATION_PREFIX = 'workflows.m3Api.sources.';

export const SELECTABLE_FIELD_SOURCES = [
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.CLIENT as string,
        icon: SOURCES_ICONS[FieldValueSource.CLIENT],
        component: defineAsyncComponent(() => import('../components/workflow-components/m3api/sources/Client.vue')),
        source: FieldValueSource.CLIENT,
        valueIsValid: () => {
            return true;
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.CLIENT;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.CLIENT;
            field.value = '';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.API as string,
        icon: SOURCES_ICONS[FieldValueSource.API],
        component: defineAsyncComponent(() => import('../components/workflow-components/m3api/sources/Api.vue')),
        source: FieldValueSource.API,
        valueIsValid: (value: string | undefined) => {
            return value !== undefined;
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.API;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.API;
            field.value = '';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.EXCEL as string,
        icon: SOURCES_ICONS[FieldValueSource.EXCEL],
        component: null,
        source: FieldValueSource.EXCEL,
        valueIsValid: () => {
            return true;
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.EXCEL;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.EXCEL;
            field.value = '';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.CONSTANT as string,
        icon: SOURCES_ICONS[FieldValueSource.CONSTANT],
        component: defineAsyncComponent(() => import('../components/workflow-components/m3api/sources/Constant.vue')),
        source: FieldValueSource.CONSTANT,
        valueIsValid: (value: string | undefined) => {
            return value !== undefined && value !== '';
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.CONSTANT;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.CONSTANT;
            field.value = '';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.EVENT as string,
        icon: SOURCES_ICONS[FieldValueSource.EVENT],
        component: defineAsyncComponent(() => import('../components/workflow-components/m3api/sources/Event.vue')),
        source: FieldValueSource.EVENT,
        valueIsValid: (value: string | undefined) => {
            return value !== undefined && value !== '';
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.EVENT;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.EVENT;
            field.value = '';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + 'CURRENT_DATE',
        icon: 'pi pi-calendar',
        component: null,
        source: FieldValueSource.CONTEXT,
        valueIsValid: () => {
            return true;
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.CONTEXT && field.value === 'currentDate';
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.CONTEXT;
            field.value = 'currentDate';
        }
    },
    {
        labelKey: STANDARD_SOURCES_LOCALIZATION_PREFIX + FieldValueSource.TAG as string,
        icon: SOURCES_ICONS[FieldValueSource.TAG],
        component: defineAsyncComponent(() => import('../components/workflow-components/m3api/sources/Tag.vue')),
        source: FieldValueSource.TAG,
        valueIsValid: (value: string | undefined) => {
            return value !== undefined && value.trim() !== '';
        },
        isSelected: (field: M3Field): boolean => {
            return field.source === FieldValueSource.TAG;
        },
        select: (field: M3Field) => {
            field.source = FieldValueSource.TAG;
            field.value = '';
        }
    }
];
